import React, { memo, useMemo } from "react";
import { Truck, AlertOctagon, Gauge } from "lucide-react";
import { StatusDot } from "../layout/StatsCard";
import { getOutOfServiceDuration } from "../../utils/helpers";

// Card-based vehicle row for virtualized list
export const VehicleRowCard = memo(({ vehicle, onClick, serviceIntervals = [], style }) => {
  // Memoize expensive calculations
  const { bitPastDue, dotPastDue, oilPastDue } = useMemo(() => {
    // Handle null/undefined vehicle inside the hook
    if (!vehicle) {
      return { bitPastDue: false, dotPastDue: false, oilPastDue: false };
    }
    
    const isServicePastDue = (lastServiceDate, lastServiceMileage, intervalName) => {
      if (!lastServiceDate) return true;
      
      const interval = serviceIntervals.find(i => 
        i.name?.toLowerCase().includes(intervalName.toLowerCase()) ||
        intervalName.toLowerCase().includes((i.name || '').toLowerCase().split(' ')[0])
      );
      
      if (!interval) return !lastServiceDate;
      
      const lastDate = new Date(lastServiceDate);
      const now = new Date();
      const daysSinceService = Math.floor((now - lastDate) / (1000 * 60 * 60 * 24));
      const milesSinceService = (vehicle.mileage || 0) - (lastServiceMileage || 0);
      
      switch (interval.interval_unit) {
        case 'days':
          return daysSinceService > interval.interval_value;
        case 'months':
          return daysSinceService > (interval.interval_value * 30);
        case 'yearly':
          return daysSinceService > (interval.interval_value * 365);
        case 'miles':
          return milesSinceService > interval.interval_value;
        case 'hours':
          return false;
        default:
          return false;
      }
    };

    const isCngOilPastDue = () => {
      if (vehicle.fuel_type !== 'cng') return false;
      if (!vehicle.last_oil_change) return true;
      const currentHours = vehicle.engine_hours || 0;
      const lastHours = vehicle.last_oil_hours || 0;
      const hoursSinceService = currentHours - lastHours;
      return hoursSinceService > 1000;
    };

    return {
      bitPastDue: isServicePastDue(vehicle.last_bit_inspection, vehicle.last_bit_mileage, "B.I.T"),
      dotPastDue: isServicePastDue(vehicle.last_dot_inspection, vehicle.last_dot_mileage, "D.O.T"),
      oilPastDue: vehicle.fuel_type === 'cng' 
        ? isCngOilPastDue() 
        : isServicePastDue(vehicle.last_oil_change, vehicle.last_oil_mileage, "Oil")
    };
  }, [vehicle, serviceIntervals]);
  
  // Guard against null/undefined vehicle - after hooks
  if (!vehicle) return null;
  
  const oosDuration = vehicle.is_out_of_service ? getOutOfServiceDuration(vehicle.out_of_service_date) : null;
  const vehicleTypeLabel = (vehicle.vehicle_type || '').replace('_', ' ');
  
  // OOS Category labels and high-contrast colors (solid backgrounds for visibility)
  const oosCategoryConfig = {
    planned_maintenance: { label: "Planned Maintenance", color: "bg-blue-600 text-white" },
    body_shop: { label: "Body Shop", color: "bg-purple-600 text-white" },
    safety_hold: { label: "Safety Hold", color: "bg-red-700 text-white" },
    breakdown: { label: "Breakdown", color: "bg-orange-500 text-white" },
    repair: { label: "Repair", color: "bg-amber-500 text-black" },
    administrative_hold: { label: "Admin Hold", color: "bg-slate-700 text-white" },
    other: { label: "Other", color: "bg-gray-500 text-white" },
  };
  const oosCategory = vehicle.out_of_service_category ? oosCategoryConfig[vehicle.out_of_service_category] : null;
  
  // Format mileage for display - handles edge cases (abbreviated for desktop)
  const formatMileage = (value) => {
    if (value === null || value === undefined || value === '') return '0';
    // Convert to number if string
    const numValue = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : Number(value);
    // Handle NaN or invalid values
    if (isNaN(numValue)) return '0';
    // Cap at reasonable display values
    if (numValue >= 1000000000) {
      return (numValue / 1000000000).toFixed(1) + 'B';
    }
    if (numValue >= 1000000) {
      return (numValue / 1000000).toFixed(1) + 'M';
    }
    if (numValue >= 10000) {
      return Math.round(numValue / 1000) + 'K';
    }
    return numValue.toLocaleString();
  };

  // Format mileage with full commas (for mobile) - 000,000 format
  const formatMileageFull = (value) => {
    if (value === null || value === undefined || value === '') return '0';
    const numValue = typeof value === 'string' ? parseFloat(value.replace(/,/g, '')) : Number(value);
    if (isNaN(numValue)) return '0';
    return numValue.toLocaleString();
  };
  
  // Format mileage timestamp as date - with error handling
  const formatMileageTimestamp = (timestamp) => {
    if (!timestamp) return null;
    try {
      const date = new Date(timestamp);
      if (isNaN(date.getTime())) return null;
      return date.toLocaleDateString('en-US', { 
        month: 'short', 
        day: 'numeric',
        year: date.getFullYear() !== new Date().getFullYear() ? 'numeric' : undefined
      });
    } catch {
      return null;
    }
  };
  
  const mileageTimestamp = formatMileageTimestamp(vehicle.mileage_updated_at);
  
  return (
    <div 
      style={style}
      className="px-1 py-1.5 sm:px-2 sm:py-1"
    >
      <div 
        className={`p-3 sm:p-4 rounded-xl border cursor-pointer transition-all shadow-sm ${
          vehicle.is_out_of_service 
            ? 'bg-red-50 border-red-200 hover:bg-red-100' 
            : 'bg-white border-slate-200 hover:border-blue-300 hover:shadow-md'
        }`}
        onClick={onClick}
        data-testid={`vehicle-card-${vehicle.id}`}
      >
        {/* Mobile Layout */}
        <div className="sm:hidden">
          {/* Row 1: Unit Number + Type + Mileage */}
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <span className="font-bold text-lg text-slate-900">{vehicle.unit_number}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${
                vehicle.vehicle_type === 'tractor' ? 'bg-blue-100 text-blue-700' :
                vehicle.vehicle_type === 'chassis' ? 'bg-green-100 text-green-700' :
                'bg-orange-100 text-orange-700'
              }`}>
                {vehicleTypeLabel}
              </span>
            </div>
            <div className="text-right">
              <div className="font-semibold text-slate-900">
                {vehicle.fuel_type === 'cng' ? (
                  <>{formatMileageFull(vehicle.engine_hours)} <span className="text-xs font-normal text-slate-500">hrs</span></>
                ) : (
                  <>{formatMileageFull(vehicle.mileage)} <span className="text-xs font-normal text-slate-500">mi</span></>
                )}
              </div>
              {mileageTimestamp && (
                <div className="text-[10px] text-slate-400">{mileageTimestamp}</div>
              )}
            </div>
          </div>
          
          {/* Row 2: Year Make Model */}
          <div className="text-sm text-slate-600 mb-2">
            {vehicle.year} {vehicle.make} {vehicle.model}
          </div>
          
          {/* Out of Service Banner */}
          {vehicle.is_out_of_service && (
            <div className={`flex items-center gap-1.5 mb-2 py-1.5 px-2 rounded-lg ${oosCategory ? oosCategory.color : 'bg-red-600 text-white'}`}>
              <AlertOctagon className="w-4 h-4 flex-shrink-0" />
              <span className="text-xs font-semibold">
                OOS: {oosCategory ? oosCategory.label : 'Unknown'}
              </span>
              {oosDuration && <span className="text-xs opacity-80">• {oosDuration}</span>}
            </div>
          )}
          
          {/* Row 3: Status Indicators */}
          <div className="flex items-center justify-between pt-2 border-t border-slate-100">
            <div className="flex items-center gap-3">
              <StatusDot status={bitPastDue ? "due" : "ok"} label="BIT" />
              <StatusDot status={dotPastDue ? "due" : "ok"} label="DOT" />
              <StatusDot status={oilPastDue ? "due" : "ok"} label="OIL" />
            </div>
            <div className="flex items-center gap-1.5 text-xs text-slate-500">
              <span className={`px-1.5 py-0.5 rounded ${
                vehicle.fuel_type === 'diesel' ? 'bg-slate-100' : 'bg-emerald-100 text-emerald-700'
              }`}>
                {vehicle.fuel_type === 'diesel' ? 'DSL' : 'CNG'}
              </span>
              {vehicle.def_equipped && (
                <span className="px-1.5 py-0.5 rounded bg-purple-100 text-purple-700">DEF</span>
              )}
              {vehicle.transmission_type && (
                <span className="px-1.5 py-0.5 rounded bg-slate-100">
                  {vehicle.transmission_type === 'automatic' ? 'A' : 'M'}
                </span>
              )}
            </div>
          </div>
        </div>

        {/* Desktop Layout */}
        <div className="hidden sm:flex items-center gap-4">
          {/* Vehicle Icon */}
          <div className={`w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0 ${
            vehicle.is_out_of_service ? 'bg-red-100' : 'bg-blue-50'
          }`}>
            <Truck className={`w-6 h-6 ${vehicle.is_out_of_service ? 'text-red-500' : 'text-blue-600'}`} />
          </div>

          {/* Main Info */}
          <div className="flex-1 min-w-0">
            <div className="flex items-center gap-2 flex-wrap">
              <span className="font-semibold text-slate-900 text-lg">{vehicle.unit_number}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full font-medium capitalize ${
                vehicle.vehicle_type === 'tractor' ? 'bg-blue-100 text-blue-700' :
                vehicle.vehicle_type === 'chassis' ? 'bg-green-100 text-green-700' :
                'bg-orange-100 text-orange-700'
              }`}>
                {vehicleTypeLabel}
              </span>
              <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                vehicle.fuel_type === 'diesel' ? 'bg-slate-100 text-slate-600' : 'bg-emerald-100 text-emerald-700'
              }`}>{vehicle.fuel_type === 'diesel' ? 'DSL' : 'CNG'}</span>
              {vehicle.def_equipped && <span className="text-xs px-2 py-0.5 rounded bg-purple-100 text-purple-700 font-medium">DEF</span>}
              {vehicle.transmission_type && <span className={`text-xs px-2 py-0.5 rounded font-medium ${
                vehicle.transmission_type === 'automatic' ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-700'
              }`}>{vehicle.transmission_type === 'automatic' ? 'AUTO' : 'STD'}</span>}
            </div>
            
            {vehicle.is_out_of_service && (
              <div className="flex items-center gap-1 mt-1">
                <span className={`inline-flex items-center gap-1 text-xs px-2 py-0.5 rounded font-semibold ${oosCategory ? oosCategory.color : 'bg-red-600 text-white'}`}>
                  <AlertOctagon className="w-3 h-3" />
                  OOS: {oosCategory ? oosCategory.label : 'Unknown'}
                  {oosDuration && <span className="opacity-80 ml-1">({oosDuration})</span>}
                </span>
              </div>
            )}
            
            <div className="text-sm text-slate-500 mt-1">
              {vehicle.year} {vehicle.make} {vehicle.model} • VIN: ...{(vehicle.vin || '').slice(-6)}
            </div>
          </div>

          {/* Mileage */}
          <div className="text-right flex-shrink-0">
            {vehicle.fuel_type === 'cng' ? (
              <>
                <div className="font-semibold text-slate-900">
                  {(vehicle.engine_hours || 0).toLocaleString()} <span className="text-xs text-slate-400">hrs</span>
                </div>
                <div className="text-xs text-slate-400">
                  {(vehicle.mileage || 0).toLocaleString()} mi
                </div>
              </>
            ) : (
              <>
                <div className="font-semibold text-slate-900">
                  {(vehicle.mileage || 0).toLocaleString()}
                </div>
                <div className="text-xs text-slate-400">
                  miles{mileageTimestamp && <span className="ml-1">• {mileageTimestamp}</span>}
                </div>
              </>
            )}
          </div>

          {/* Status Dots */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <StatusDot status={bitPastDue ? "due" : "ok"} label="BIT" />
            <StatusDot status={dotPastDue ? "due" : "ok"} label="DOT" />
            <StatusDot status={oilPastDue ? "due" : "ok"} label="OIL" />
          </div>
        </div>
      </div>
    </div>
  );
});

VehicleRowCard.displayName = 'VehicleRowCard';
